import { CssCustomPropertyItem } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import {
  CssPropertyType,
  LeadingStyleGroup,
  ComparisonMode,
} from '../../../types';
import { getSettersMap } from './deriveSettersConfig';
import { SetterConfig, StyleValueKey, SetterData } from '../types';
import { STYLE_NAMESPACE } from '../../../../../apis/services/editActionsServices/nativeActions/globalDesign/types';

const addSetter = (
  setters: Record<StyleValueKey, SetterConfig[]>,
  sourcePropertyKey: StyleValueKey,
  setterConfig: SetterConfig,
) => {
  if (!setters[sourcePropertyKey]) {
    setters[sourcePropertyKey] = [];
  }
  setters[sourcePropertyKey].push(setterConfig);
};

const addEquivalentSetters = (
  setters: Record<StyleValueKey, SetterConfig[]>,
  cssPropertyType: CssPropertyType,
  setterData: SetterData,
) => {
  // Get value-equivalent setters from SETTERS_MAP
  const equivalentSettersMap = getSettersMap()[cssPropertyType]?.equivalents;
  if (!equivalentSettersMap || Object.keys(equivalentSettersMap).length === 0) {
    return;
  }

  Object.entries(equivalentSettersMap).forEach(
    ([equivalentPropertyType, setterGetters]) => {
      if (!setterGetters) {
        return;
      }

      const sourceKey = equivalentPropertyType as StyleValueKey;

      setterGetters.forEach((setterGetter) => {
        addSetter(setters, sourceKey, setterGetter(setterData));
      });
    },
  );
};

export const getSetters = (
  subsetGroups: LeadingStyleGroup['subsetGroups'],
  comparisonMode: ComparisonMode,
) => {
  const setters = {} as Record<StyleValueKey, SetterConfig[]>;

  // Process all items from all subset groups
  subsetGroups.forEach((group) => {
    group.items.forEach((item) => {
      const cssPropertyType = (
        group.styleNamespace === STYLE_NAMESPACE.CssCustomProperties
          ? (item.styleItem as CssCustomPropertyItem).cssPropertyType
          : item.styleItemId
      ) as CssPropertyType;

      if (!cssPropertyType) {
        return;
      }

      const settersConfigGetters = getSettersMap()[cssPropertyType];
      if (!settersConfigGetters) {
        return;
      }

      const setterData: SetterData = {
        cssPropertyType,
        styleItemId: item.styleItemId,
        editorPointer: group.editorPointer,
        context: group.context,
        engine: group.engine,
        styleNamespace: group.styleNamespace,
      };

      // Create setters for standard targets (self, shorthands, parents)
      Object.entries(settersConfigGetters.standard).forEach(
        ([targetKey, setterConfigGetters]) => {
          if (!setterConfigGetters) {
            return;
          }
          setterConfigGetters.forEach((setterConfigGetter) => {
            addSetter(
              setters,
              targetKey as StyleValueKey,
              setterConfigGetter(setterData),
            );
          });
        },
      );

      // In ValueSet mode: add value-equivalent setters for both CssProperties and CssCustomProperties
      // The logic is the same: use cssPropertyType as source key, setterData contains the target
      // For custom properties: cssPropertyType is from the item's type, styleItemId is the custom property name
      if (comparisonMode === ComparisonMode.ValueSet) {
        addEquivalentSetters(setters, cssPropertyType, setterData);
      }
    });
  });

  return setters;
};
